import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, Feather, ShieldCheck, Compass } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";

const AboutPage = () => {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-24 max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="overline mb-6">About the Publication</div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-display font-normal text-ink leading-[0.98] tracking-[-0.02em] text-5xl md:text-7xl lg:text-[92px] max-w-6xl"
        >
          A publication built for the <span className="italic">long horizon</span>.
        </motion.h1>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <p className="lg:col-span-8 text-ink-soft text-lg md:text-xl leading-relaxed">
            World Visionary Times is an editorial platform reporting on global leaders, business, innovation and technology. We publish long-form articles, extended interviews and a printed magazine designed to sit beside the enduring international titles — with the discipline of a traditional editorial masthead and the reach of the modern web.
          </p>
          <div className="lg:col-span-4">
            <div className="hairline-strong mb-6 max-w-[80px]" />
            <div className="overline overline-muted mb-3">Founded</div>
            <div className="font-display text-ink text-3xl">Feb 2026</div>
            <div className="mt-6 overline overline-muted mb-3">Headquarters</div>
            <div className="text-ink text-sm">Global · Distributed Editorial Desk</div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24 md:py-32 bg-cream border-y border-ink-line">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-14">
          <div>
            <div className="overline mb-5">Our Mission</div>
            <h2 className="font-display text-ink text-4xl md:text-5xl leading-[1.06] mb-6">To report on the people quietly rewiring global industry.</h2>
            <p className="text-ink-soft text-lg leading-relaxed">
              We exist to publish the kind of considered, unhurried reporting that has grown scarce inside the daily news cycle. Our mission is to identify the operators, founders and institutions building enduring companies and to give them the pages, the interviews and the attention their work deserves.
            </p>
          </div>
          <div>
            <div className="overline mb-5">Our Vision</div>
            <h2 className="font-display text-ink text-4xl md:text-5xl leading-[1.06] mb-6">A durable global title, edited with care.</h2>
            <p className="text-ink-soft text-lg leading-relaxed">
              A generation from now, we intend for World Visionary Times to sit alongside the international publications that people still read on paper — trusted for editorial judgment, admired for craft, and consulted for its long-standing coverage of the leaders shaping the modern economy.
            </p>
          </div>
        </div>
      </section>

      {/* Editorial Values */}
      <section className="py-24 md:py-32 max-w-[1440px] mx-auto px-5 md:px-10">
        <SectionHeader overline="Our Standards" title="Editorial values, plainly stated." />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Feather, title: "Original reporting", body: "We do not repackage press releases. Every article, interview and news dispatch is written from source by our editorial team." },
            { icon: ShieldCheck, title: "Verified accuracy", body: "Facts, figures and quotations are verified before publication. Corrections are transparent and prominent when necessary." },
            { icon: Globe, title: "Global perspective", body: "We report from a genuinely international editorial desk — with operators, founders and policy voices represented across geographies." },
            { icon: Compass, title: "Long-horizon lens", body: "We favour reporting that will still be worth reading in five years over commentary optimised for the next twelve hours." },
          ].map((v, i) => (
            <motion.div
              key={v.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className="editorial-card p-8"
              data-testid={`value-card-${i}`}
            >
              <v.icon size={22} strokeWidth={1.2} className="text-ink" />
              <div className="mt-6 font-display text-ink text-xl md:text-2xl leading-tight">{v.title}</div>
              <p className="mt-4 text-ink-soft text-sm leading-relaxed">{v.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-24 md:py-32 bg-cream border-y border-ink-line">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeader overline="Why Choose Us" title="Considered. Verified. Enduring." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Editorial independence", d: "Our editorial decisions are made independently of commercial arrangements. Advertisers and partners are never granted influence over what we publish." },
              { n: "02", t: "Depth over cadence", d: "We publish less than most competing outlets, and take longer to publish each piece. That trade-off is deliberate and non-negotiable." },
              { n: "03", t: "Global desk", d: "Correspondents and contributors report from the geographies they cover, not from a single home office reflecting a single vantage point." },
              { n: "04", t: "Craft in production", d: "Photography, typography, print and web are treated with the same attention as the writing itself. The publication is designed to feel as considered as it reads." },
              { n: "05", t: "Access to leaders", d: "Our interview subjects include founders and chief executives of significant international organisations, drawn from an editorial network built over multiple careers." },
              { n: "06", t: "Reader-first", d: "Our business model prioritises reader trust over the volume metrics that shape the rest of the digital publication landscape." },
            ].map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="editorial-card p-8"
                data-testid={`why-us-${c.n}`}
              >
                <div className="number-shadow text-5xl leading-none mb-6">{c.n}</div>
                <div className="font-display text-ink text-2xl leading-tight">{c.t}</div>
                <p className="mt-3 text-ink-soft text-sm leading-relaxed">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial Process */}
      <section className="py-24 md:py-32 max-w-[1440px] mx-auto px-5 md:px-10">
        <SectionHeader overline="Editorial Process" title="How we decide what to publish." />
        <div className="mt-14 space-y-8">
          {[
            { step: "01", title: "Commissioning", body: "Ideas originate with our editors and correspondents. Every commission includes a clear thesis, a set of intended interviewees and an editorial angle vetted against our standards." },
            { step: "02", title: "Reporting", body: "Reporting is done from source. Interviews, on-the-record conversations and primary documents form the substance of what we publish." },
            { step: "03", title: "Editing", body: "Every piece passes through at least two editorial readers before publication. Structural editing, line editing and copy editing are treated as distinct disciplines." },
            { step: "04", title: "Verification", body: "Facts, figures and quotations are checked against source material. Where verification is not possible, the point is either removed or clearly framed." },
            { step: "05", title: "Publication", body: "Pieces are published when they are ready, not when a schedule demands. Our cadence is subordinated to the quality of the work." },
          ].map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 border-b border-ink-line pb-8"
              data-testid={`process-step-${s.step}`}
            >
              <div className="md:col-span-2 font-display text-ink text-4xl md:text-5xl font-light">{s.step}</div>
              <div className="md:col-span-3 font-display text-ink text-2xl md:text-3xl">{s.title}</div>
              <div className="md:col-span-7 text-ink-soft text-base md:text-lg leading-relaxed">{s.body}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Our publications */}
      <section className="py-24 md:py-32 bg-cream border-y border-ink-line">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeader overline="Our Publications" title="Three surfaces, one editorial voice." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { t: "The Print Magazine", d: "A growing archive of flagship editions produced with care and printed on paper you will want to keep on a shelf.", cta: "See editions", to: "/magazine" },
              { t: "The Digital Long-Form", d: "Original articles and interviews, arriving without algorithmic distortion, delivered directly through the site and the Editor\u2019s Note.", cta: "Read articles", to: "/articles" },
              { t: "The Daily Dispatch", d: "A concise digest of the news that matters to our readers, curated by our editorial desk rather than by a feed.", cta: "See dispatches", to: "/news" },
            ].map((p, i) => (
              <div key={p.t} className="editorial-card p-8" data-testid={`publication-${i}`}>
                <div className="overline overline-muted">Surface — 0{i + 1}</div>
                <div className="mt-6 font-display text-ink text-2xl md:text-3xl">{p.t}</div>
                <p className="mt-4 text-ink-soft text-sm leading-relaxed">{p.d}</p>
                <Link to={p.to} className="mt-6 inline-flex items-center gap-2 text-ink text-sm font-medium border-b border-ink pb-0.5 hover:text-gold-dark hover:border-gold-dark transition-colors" data-testid={`publication-cta-${i}`}>{p.cta} <ArrowRight size={14} /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 max-w-[1440px] mx-auto px-5 md:px-10 text-center">
        <div className="overline mb-6">Have a story worth telling?</div>
        <h2 className="font-display text-ink text-4xl md:text-6xl leading-[1.05] max-w-3xl mx-auto">Nominate a leader, pitch a story or apply to be featured.</h2>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link data-testid="about-cta-featured" to="/get-featured" className="px-8 py-4 bg-ink text-paper font-medium text-sm tracking-wide rounded-full hover:bg-gold-dark transition-colors">Get Featured</Link>
          <Link data-testid="about-cta-contact" to="/contact" className="px-8 py-4 border border-ink text-ink font-medium text-sm tracking-wide rounded-full hover:bg-ink hover:text-paper transition-colors">Contact the Desk</Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
